import { useState, useEffect } from 'react';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { api } from '../api/client';
import aideyLogo from '../assets/sidebar-logo.svg';
import TrialConfirm from './StartTrial/TrialConfirm';          // card at sign-up, phase 2
import { browserTimeZone, PLAN_KEYS } from './StartTrial/startTrial';

const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';

// Detect browser locale for number formatting only — billing currency is set in Stripe
const userLocale = typeof navigator !== 'undefined' ? navigator.language : 'en-GB';

function formatPrice(amount, currency) {
  if (amount == null) return '—';
  return new Intl.NumberFormat(userLocale, {
    style: 'currency', currency: currency || 'GBP',
    minimumFractionDigits: 0, maximumFractionDigits: 2,
  }).format(amount);
}

const PLAN_META = {
  Basics: {
    label: 'Basic',
    description: 'For freelancers getting started. Core task and client tools with a simple financial summary.',
    features: ['Tasks & scheduling', 'Client contacts', '10 invoices per month', 'Email inbox', '1 user'],   // UI-24 item 19: invoices are in Basics
    color: '#4b5563',
    badge: null,
  },
  Pro: {
    label: 'Pro',
    description: 'Everything in Basic, plus unlimited invoicing, purchase orders, expenses, tax summary and Xero, proposals, and AI features.',
    features: ['Everything in Basic', 'Unlimited invoicing, purchase orders, expenses, tax summary and Xero', 'Financial charts', 'Proposals & contracts', 'AI task assistance', 'Scheduling & booking links'],
    color: NAVY,
    badge: 'Most popular',
  },
  Team: {
    label: 'Team Pro',
    description: 'Everything in Pro, plus team management, HR, shared calendars, and per-seat billing.',
    features: ['Everything in Pro', 'Team management & HR', 'Shared team calendar', 'Leave & time tracking', 'Per-seat billing (min 2 seats)'],
    color: '#7c3aed',
    badge: null,
  },
  Personal: {
    label: 'Personal',
    description: 'Aidey for everyday life.',
    features: ['Personal tasks & calendar', 'Budgeting & spending tracker', 'Meal planning', 'Shopping list', 'Journal', '1 user'],
    color: '#0891b2',
    badge: null,
    personalOnly: true,
  },
  Family: {
    label: 'Family',
    description: 'Aidey for everyday life, shared with your household.',
    features: ['Everything in Personal', 'Up to 6 household members', 'Shared family calendar', 'Household shopping list', 'Kids mode', 'Family budget view'],
    color: '#0891b2',
    badge: null,
    personalOnly: true,
  },
};

// Decision 3 (Carla, round 3, 2026-10-12): seats follow the member count. A Team plan starts at
// its two seat minimum and the server keeps it at the member count (lib/teamBilling); there is no
// seat picker here, and checkout is not sent a quantity.
const TEAM_MIN_SEATS = 2;

export default function PlanSelectPage() {
  const { currentUser, logout, loading } = useCurrentUser();
  const [billing, setBilling] = useState('annual');
  const [prices, setPrices] = useState(null);
  const [pricesError, setPricesError] = useState(false);
  const [personalPlansEnabled, setPersonalPlansEnabled] = useState(false);
  const [error, setError] = useState('');
  // Card at sign-up (phase 2): the trial THIS account would get at checkout, from the server
  // (GET /api/billing/trial-offer): TRIAL_DAYS and its end date for a new account, what is left of
  // the trial for an older one, none when it has ended. Not today + a written 14.
  const [offer, setOffer] = useState(null);
  // The confirm step: a plan chosen, the sentence shown, then Stripe Checkout.
  const [confirming, setConfirming] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      const plan = q.get('plan');
      const period = q.get('period');
      if (PLAN_KEYS.includes(plan)) return { planKey: plan, billingPeriod: period === 'annual' ? 'annual' : 'monthly' };
    } catch { /* no address to read */ }
    return null;
  });
  const hasTrial = !!(offer && offer.trialEndText && offer.daysLeft >= 1);
  const trialDays = offer && Number.isFinite(Number(offer.trialDays)) ? Number(offer.trialDays) : null;

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/api/billing/prices`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        setPrices(d.prices || {});
        setPersonalPlansEnabled(d.personalPlansEnabled === true);
      })
      .catch(() => setPricesError(true));
  }, []);

  useEffect(() => {
    if (loading || !currentUser) return;
    const tz = encodeURIComponent(browserTimeZone());
    api(`/api/billing/trial-offer?timeZone=${tz}`)
      .then((r) => r.json())
      .then((d) => setOffer(d || null))
      .catch(() => setOffer(null));
  }, [loading, currentUser]);

  useEffect(() => {
    if (!loading && !currentUser) {
      window.location.href = '/';
    }
  }, [loading, currentUser]);

  if (loading || !currentUser) return null;

  if (currentUser.isCoveredMember) {
    return (
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(150deg, ${NAVY} 0%, #1a4a7a 50%, #0a2038 100%)`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: '2rem 1rem', fontFamily: 'Raleway, sans-serif',
      }}>
        <img src={aideyLogo} alt="Aidey" style={{ height: 44, marginBottom: '1.5rem' }} />
        <div style={{
          background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.13)',
          borderRadius: 16, padding: '2.5rem 2rem', maxWidth: 420, textAlign: 'center',
        }}>
          <div style={{ fontSize: 36, marginBottom: '1rem' }}>✓</div>
          <h2 style={{ color: '#fff', fontFamily: 'Poppins, sans-serif', fontSize: '1.35rem', fontWeight: 800, margin: '0 0 0.75rem' }}>
            You're all set
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.95rem', margin: '0 0 1.75rem' }}>
            Your access is included in your family's plan, there's nothing to set up here.
          </p>
          <button
            onClick={() => { window.location.href = '/'; }}
            style={{
              padding: '0.75rem 2rem', background: YELLOW, color: NAVY,
              border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '1rem',
              cursor: 'pointer', fontFamily: 'Raleway, sans-serif',
            }}
          >
            Go to Aidey
          </button>
        </div>
      </div>
    );
  }

  function getPriceInfo(planKey) {
    if (!prices) return null;
    const key = `${planKey}_${billing}`.toLowerCase();
    return prices[key] || null;
  }

  function displayPrice(planKey) {
    const info = getPriceInfo(planKey);
    if (pricesError) return null;
    if (!info) return '…';
    const amount = planKey === 'Team' ? info.amount * TEAM_MIN_SEATS : info.amount;
    return `${formatPrice(amount, info.currency)}/${billing === 'annual' ? 'yr' : 'mo'}`;
  }

  function perSeatDisplay(planKey) {
    if (planKey !== 'Team') return null;
    const info = getPriceInfo(planKey);
    if (!info) return null;
    return `${formatPrice(info.amount, info.currency)}/seat/${billing === 'annual' ? 'yr' : 'mo'}`;
  }

  // Choosing a plan opens the CONFIRM step (card at sign-up, phase 2): what will be charged and
  // when, from Stripe, above the button that opens Checkout. Nothing is opened at Stripe until then.
  function handleSelect(planKey) {
    setError('');
    setConfirming({ planKey, billingPeriod: billing });
  }

  if (confirming) {
    return (
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(150deg, ${NAVY} 0%, #1a4a7a 50%, #0a2038 100%)`,
        padding: '2rem 1rem', fontFamily: 'Raleway, sans-serif', boxSizing: 'border-box',
      }}>
        <img src={aideyLogo} alt="Aidey" style={{ height: 44, display: 'block', margin: '0 auto 1.5rem' }} />
        <div className="st-confirm-wrap">
          <TrialConfirm
            planName={confirming.planKey}
            planLabel={PLAN_META[confirming.planKey]?.label || confirming.planKey}
            billingPeriod={confirming.billingPeriod}
            onBack={() => setConfirming(null)}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(150deg, ${NAVY} 0%, #1a4a7a 50%, #0a2038 100%)`,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '2rem 1rem', fontFamily: 'Raleway, sans-serif',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <img src={aideyLogo} alt="Aidey" style={{ height: 44, marginBottom: '1.5rem', display: 'block', margin: '0 auto 1.25rem' }} />
        <h1 style={{ color: '#fff', fontFamily: 'Poppins, sans-serif', fontSize: '1.65rem', fontWeight: 800, margin: '0 0 0.5rem' }}>
          Choose your plan
        </h1>
        {hasTrial && (
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.95rem', margin: 0 }} data-testid="plan-trial-line">
            {offer.daysLeft === trialDays
              ? <>Every plan starts with a <strong style={{ color: YELLOW }}>{trialDays}-day free trial</strong>: no charge until {offer.trialEndText}.</>
              : <>Your free trial runs until <strong style={{ color: YELLOW }}>{offer.trialEndText}</strong>: no charge until then.</>}
          </p>
        )}
        {currentUser?.name && (
          <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', marginTop: '0.35rem' }}>
            Signed in as {currentUser.email}
          </p>
        )}
      </div>

      {/* Billing toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        background: 'rgba(255,255,255,0.08)', borderRadius: 24,
        padding: '0.35rem', marginBottom: '2rem',
      }}>
        {['monthly', 'annual'].map(b => (
          <button
            key={b}
            onClick={() => setBilling(b)}
            style={{
              padding: '0.45rem 1.25rem', border: 'none', borderRadius: 20,
              fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
              fontFamily: 'Raleway, sans-serif', transition: 'all 0.15s',
              background: billing === b ? YELLOW : 'transparent',
              color: billing === b ? NAVY : 'rgba(255,255,255,0.7)',
            }}
          >
            {b === 'monthly' ? 'Monthly' : 'Annual'}
          </button>
        ))}
      </div>

      {/* Plan cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))',
        gap: '1.25rem', width: '100%', maxWidth: 920,
      }}>
        {Object.entries(PLAN_META).filter(([, meta]) => !meta.personalOnly || personalPlansEnabled).map(([planKey, meta]) => {
          const priceStr = displayPrice(planKey);
          const perSeat  = perSeatDisplay(planKey);
          const savingAmount = (() => {
            if (billing !== 'annual' || !prices) return null;
            const mo = prices[`${planKey.toLowerCase()}_monthly`];
            const yr = prices[`${planKey.toLowerCase()}_annual`];
            if (!mo || !yr) return null;
            const s = mo.amount * 12 - yr.amount;
            return s > 0 ? { amount: s, currency: mo.currency } : null;
          })();
          return (
            <div key={planKey} style={{
              background: '#fff', borderRadius: 16,
              padding: '1.75rem 1.5rem',
              border: meta.badge ? `2px solid ${NAVY}` : '2px solid transparent',
              position: 'relative', display: 'flex', flexDirection: 'column',
            }}>
              {meta.badge && (
                <div style={{
                  position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                  background: NAVY, color: '#fff', fontSize: '0.72rem', fontWeight: 700,
                  padding: '0.25rem 0.9rem', borderRadius: 20, whiteSpace: 'nowrap',
                  fontFamily: 'Poppins, sans-serif', letterSpacing: '0.04em',
                }}>
                  {meta.badge}
                </div>
              )}

              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.35rem' }}>
                {meta.label}
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: 900, color: NAVY, fontFamily: 'Poppins, sans-serif', marginBottom: '0.15rem' }}>
                {priceStr}
              </div>
              {perSeat && (
                <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: '0.2rem' }}>
                  {perSeat} × {TEAM_MIN_SEATS} seats to start
                </div>
              )}
              {savingAmount && (
                <div style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 700, marginBottom: '0.35rem' }}>
                  Save {formatPrice(savingAmount.amount, savingAmount.currency)}{planKey === 'Team' ? '/user' : ''}/yr
                </div>
              )}
              <p style={{ fontSize: '0.82rem', color: '#555', lineHeight: 1.5, margin: '0.5rem 0 1rem', flex: 1 }}>
                {meta.description}
              </p>

              {planKey === 'Team' && (
                <p style={{ fontSize: '0.78rem', color: '#555', lineHeight: 1.5, margin: '0 0 1rem' }}>
                  Seats follow your member count, two at least: a seat is added when someone joins your team and removed when they leave.
                </p>
              )}

              <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                {meta.features.map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.82rem', color: '#444' }}>
                    <span style={{ color: '#16a34a', fontWeight: 700, flexShrink: 0 }}>✓</span> {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSelect(planKey)}
                style={{
                  width: '100%', padding: '0.75rem',
                  background: meta.badge ? NAVY : '#374151',
                  color: '#fff', border: 'none', borderRadius: 9,
                  fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
                  fontFamily: 'Poppins, sans-serif', transition: 'background 0.15s',
                }}
              >
                {hasTrial ? 'Start free trial' : 'Choose this plan'}
              </button>
              <p style={{ textAlign: 'center', fontSize: '0.72rem', color: '#6b7280', margin: '0.5rem 0 0' }}>
                {hasTrial && offer.daysLeft === trialDays ? `${trialDays}-day free trial · ` : ''}Cancel any time
              </p>
            </div>
          );
        })}
      </div>

      {/* Enterprise stays contact-us */}
      <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem', marginTop: '1.5rem', textAlign: 'center' }}>
        Enterprise: email <a href="mailto:support@aideyapp.com" style={{ color: YELLOW, fontWeight: 700 }}>support@aideyapp.com</a> and we will set it up with you.
      </p>

      {/* Trial reminder */}
      {hasTrial && (
        <div style={{
          marginTop: '1rem', background: 'rgba(255,255,255,0.07)',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
          padding: '0.9rem 1.5rem', maxWidth: 620, textAlign: 'center',
          color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', lineHeight: 1.6,
        }}>
          When your free trial ends on {offer.trialEndText}, you will be charged for your selected plan. Cancel any time before then and you will not be charged. The next step shows the exact amount.
        </div>
      )}

      {error && (
        <div style={{
          marginTop: '1rem', background: '#fde8e8', border: '1px solid #f5a5a5',
          borderRadius: 8, padding: '0.65rem 1rem', fontSize: '0.85rem',
          color: '#A12710', maxWidth: 500, textAlign: 'center',
        }}>
          ⚠ {error}
        </div>
      )}

      {pricesError && (
        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', marginTop: '1rem' }}>
          Could not load live prices, contact support if this persists.
        </p>
      )}

      <button
        onClick={() => logout()}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem', marginTop: '1.5rem',
          textDecoration: 'underline', fontFamily: 'Raleway, sans-serif',
        }}
      >
        Log out
      </button>
    </div>
  );
}
